/**
 * Money utility: integer cents storage to avoid floating-point issues in financial calculations.
 */

import type { Lease } from '@repo/shared-types';

export class SchemaViolationError extends Error {
  readonly code = 'SCHEMA_VIOLATION';
  readonly fieldPath?: string;
  readonly value?: unknown;

  constructor(message: string, options?: { fieldPath?: string; value?: unknown }) {
    super(message);
    this.name = 'SchemaViolationError';
    this.fieldPath = options?.fieldPath;
    this.value = options?.value;
  }
}

export type CentsScalar = string | number | bigint | null | undefined;

function centsScalarToBigInt(value: CentsScalar, fieldPath?: string): bigint {
  if (value == null) return 0n;
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new SchemaViolationError('Expected finite number for cents scalar', { fieldPath, value });
    }
    if (!Number.isInteger(value)) {
      throw new SchemaViolationError('Expected integer number for cents scalar', { fieldPath, value });
    }
    return BigInt(value);
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) return 0n;
  if (trimmed.includes('.')) {
    throw new SchemaViolationError('Decimal strings are invalid for cents transport values', {
      fieldPath,
      value,
    });
  }
  if (!/^-?\d+$/.test(trimmed)) {
    throw new SchemaViolationError('Invalid cents transport string format', { fieldPath, value });
  }

  try {
    return BigInt(trimmed);
  } catch {
    throw new SchemaViolationError('Unable to parse cents transport string as BigInt', { fieldPath, value });
  }
}

export function parseCentsScalar(value: CentsScalar, fieldPath?: string): number {
  const cents = centsScalarToBigInt(value, fieldPath);
  const max = BigInt(Number.MAX_SAFE_INTEGER);
  const min = BigInt(Number.MIN_SAFE_INTEGER);
  if (cents > max || cents < min) {
    throw new SchemaViolationError('Cents scalar exceeds Number safe integer range', { fieldPath, value });
  }
  return Number(cents);
}

export function centsScalarToDollars(value: CentsScalar, fieldPath?: string): number {
  return parseCentsScalar(value, fieldPath) / 100;
}

export function formatCentsScalar(
  value: CentsScalar,
  options?: { showCents?: boolean; currency?: string; locale?: string },
  fieldPath?: string,
): string {
  const cents = parseCentsScalar(value, fieldPath);
  return Money.fromCents(cents).format(options);
}

export class Money {
  private readonly cents: number;

  private constructor(cents: number) {
    if (!Number.isInteger(cents)) {
      throw new Error('Money amount must be an integer (cents)');
    }
    this.cents = cents;
  }

  static fromDollars(dollars: number): Money {
    if (isNaN(dollars) || !isFinite(dollars)) {
      throw new Error('Invalid dollar amount');
    }
    const cents = Math.round(dollars * 100);
    return new Money(cents);
  }

  static fromCents(cents: number): Money {
    if (!Number.isInteger(cents)) {
      throw new Error('Cents must be an integer');
    }
    return new Money(cents);
  }

  static fromString(dollarsStr: string): Money {
    const dollars = Number.parseFloat(dollarsStr);
    if (isNaN(dollars)) {
      throw new Error(`Invalid money string: ${dollarsStr}`);
    }
    return Money.fromDollars(dollars);
  }

  getCents(): number {
    return this.cents;
  }

  getDollars(): number {
    return this.cents / 100;
  }

  add(other: Money): Money {
    return new Money(this.cents + other.cents);
  }

  subtract(other: Money): Money {
    return new Money(this.cents - other.cents);
  }

  multiply(factor: number): Money {
    if (isNaN(factor) || !isFinite(factor)) {
      throw new Error('Invalid multiplication factor');
    }
    return new Money(Math.round(this.cents * factor));
  }

  divide(divisor: number): Money {
    if (isNaN(divisor) || !isFinite(divisor) || divisor === 0) {
      throw new Error('Invalid divisor');
    }
    return new Money(Math.round(this.cents / divisor));
  }

  percentage(percentage: number): Money {
    return this.multiply(percentage / 100);
  }

  compare(other: Money): number {
    if (this.cents < other.cents) return -1;
    if (this.cents > other.cents) return 1;
    return 0;
  }

  equals(other: Money): boolean {
    return this.cents === other.cents;
  }

  greaterThan(other: Money): boolean {
    return this.cents > other.cents;
  }

  lessThan(other: Money): boolean {
    return this.cents < other.cents;
  }

  greaterThanOrEqual(other: Money): boolean {
    return this.cents >= other.cents;
  }

  lessThanOrEqual(other: Money): boolean {
    return this.cents <= other.cents;
  }

  isZero(): boolean {
    return this.cents === 0;
  }

  isNegative(): boolean {
    return this.cents < 0;
  }

  abs(): Money {
    return new Money(Math.abs(this.cents));
  }

  round(): Money {
    return this;
  }

  format(options?: {
    showCents?: boolean;
    currency?: string;
    locale?: string;
  }): string {
    const dollars = this.getDollars();
    const showCents = options?.showCents !== false;
    const currency = options?.currency || 'USD';
    const locale = options?.locale || 'en-US';

    if (showCents) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(dollars);
    }
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(dollars));
  }

  toJSON(): number {
    return this.getDollars();
  }

  toString(): string {
    return this.getDollars().toFixed(2);
  }

  toDisplayValue(): number {
    return Math.round(this.cents) / 100;
  }
}

export function sumMoney(...amounts: Money[]): Money {
  const totalCents = amounts.reduce((sum, money) => sum + money.getCents(), 0);
  return Money.fromCents(totalCents);
}

export function averageMoney(...amounts: Money[]): Money {
  if (amounts.length === 0) {
    return Money.fromCents(0);
  }
  return sumMoney(...amounts).divide(amounts.length);
}

export function calculatePercentage(amount: Money, percentage: number): Money {
  return amount.percentage(percentage);
}

export function roundToCents(dollars: number): number {
  return Math.round(dollars * 100) / 100;
}

export function centsToDollars(cents: number): number {
  return cents / 100;
}

export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}

/**
 * Parse user money input (`$500.25`, `500.25`, `500`) into integer USD cents.
 * Blank → null. Rejects negatives and more than 2 decimal places (no float * 100).
 */
export function parseUserMoneyToCents(input: string | null | undefined): number | null {
  if (input == null) return null;
  const trimmed = String(input).trim();
  if (trimmed.length === 0) return null;
  const normalized = trimmed.replace(/\$/g, '').replace(/,/g, '').trim();
  if (normalized.length === 0) return null;
  if (normalized.startsWith('-')) {
    throw new SchemaViolationError('Money amount must be non-negative', { fieldPath: 'money', value: input });
  }
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
    throw new SchemaViolationError('Invalid money format (use up to 2 decimal places)', {
      fieldPath: 'money',
      value: input,
    });
  }
  const [wholePart, fracPart = ''] = normalized.split('.');
  const whole = Number.parseInt(wholePart ?? '0', 10);
  const frac = fracPart.padEnd(2, '0').slice(0, 2);
  const cents = whole * 100 + Number.parseInt(frac || '0', 10);
  if (!Number.isFinite(cents) || !Number.isInteger(cents) || cents < 0) {
    throw new SchemaViolationError('Unable to parse money as integer cents', { fieldPath: 'money', value: input });
  }
  return cents;
}

export function formatCentsAsCurrency(
  cents: number,
  options?: { showCents?: boolean; currency?: string; locale?: string }
): string {
  return Money.fromCents(cents).format(options);
}

export function formatDollarsAsCurrency(
  dollars: number,
  options?: { showCents?: boolean; currency?: string; locale?: string }
): string {
  return Money.fromDollars(dollars).format(options);
}

/** Canonical monthly rent as integer cents (`rentAmountCents` when present, else legacy `monthlyRent` dollars). */
export function leaseMonthlyRentCents(lease: Pick<Lease, 'rentAmountCents' | 'monthlyRent'>): number {
  if (lease.rentAmountCents != null) return lease.rentAmountCents;
  return dollarsToCents(lease.monthlyRent ?? 0);
}

/**
 * Coerce Prisma / JS cents scalars to bigint (null/undefined → 0n; non-finite numbers → 0n; integers truncated toward zero).
 * Used by reporting and close workflows for safe bigint arithmetic.
 */
export function toBigIntCents(value: number | bigint | null | undefined): bigint {
  if (value == null) return 0n;
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return 0n;
    return BigInt(Math.trunc(value));
  }
  return 0n;
}

export function sumBigIntCents(values: ReadonlyArray<number | bigint | null | undefined>): bigint {
  return values.reduce<bigint>((sum, value) => sum + toBigIntCents(value), 0n);
}

/**
 * Prisma `_sum.amountCents` on Bill/Receivable/Payment (bigint or number cents, or null when empty) → dollar number.
 */
export function prismaAggregateCentsToDollars(sum: bigint | number | null | undefined): number {
  if (sum == null) return 0;
  if (typeof sum === 'bigint') return Number(sum) / 100;
  if (typeof sum === 'number' && Number.isFinite(sum)) return sum / 100;
  return 0;
}

/**
 * Sum integer cents across receivable/payment rows (`amountCents`) and bill rows with `amountCents` or legacy `amount` in dollars.
 */
export function sumRowAmountCents(rows: ReadonlyArray<object>): bigint {
  return rows.reduce((s, r) => {
    const row = r as Record<string, unknown>;
    const ac = row.amountCents;
    if (ac != null && ac !== '') {
      if (typeof ac === 'bigint') return s + ac;
      if (typeof ac === 'number' && Number.isFinite(ac)) return s + BigInt(Math.round(ac));
    }
    const a = row.amount as number | undefined;
    if (typeof a === 'number' && Number.isFinite(a)) {
      return s + BigInt(Math.round(a * 100));
    }
    return s;
  }, 0n);
}
